import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle, Globe, Lightning, Lock, MagnifyingGlass, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

const STEPS = [
  { icon: MagnifyingGlass, label: "SEO" },
  { icon: Lightning, label: "Performance" },
  { icon: ShieldCheck, label: "Security" },
  { icon: CheckCircle, label: "Accessibility" },
  { icon: Globe, label: "Tech stack" },
];

export default function NewAuditPage() {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(null);
  const nav = useNavigate();
  const { billing, refreshBilling } = useAuth();
  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    setPaywall(null);
    try {
      const { data } = await api.post("/audits", { url: url.trim() });
      toast.success("Audit queued. Running…");
      refreshBilling();
      nav(`/app/audit/${data.id}`);
    } catch (err) {
      if (err?.response?.status === 402) {
        setPaywall(err.response.data?.detail || "Monthly limit reached.");
      } else {
        toast.error(err?.response?.data?.detail || "Failed to start audit");
      }
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl">
        <div className="label-overline mb-2">New audit</div>
        <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-none mb-3">
          Audit any website.
        </h1>
        <p className="text-ink-muted mb-10 text-lg">
          Paste a URL — we will fetch it, run sixty checks, then return a
          prioritised fix-list you can act on today.
        </p>

        {paywall && (
          <div className="surface-card hard-shadow-sm bg-warning/5 border-warning p-6 mb-6" data-testid="paywall-banner">
            <div className="flex items-start gap-3">
              <Lock size={22} className="text-warning shrink-0 mt-0.5" weight="duotone" />
              <div className="flex-1">
                <div className="font-display font-bold text-lg mb-1">Plan limit reached</div>
                <p className="text-sm text-ink-muted mb-4">{paywall}</p>
                <Link to="/app/billing" data-testid="paywall-upgrade-link"
                  className="inline-flex items-center gap-2 bg-ink text-white px-4 py-2 text-sm rounded-sm font-semibold hover:bg-brand transition-colors">
                  Upgrade or buy credits <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        )}

        {billing && billing.usage.monthly_cap > 0 && (
          <div className="flex items-center justify-between mb-3 text-xs text-ink-muted mono">
            <span>{billing.plan_label} · {billing.usage.audits_this_period}/{billing.usage.monthly_cap === -1 ? "∞" : billing.usage.monthly_cap} audits used</span>
            {billing.credits > 0 && <span>{billing.credits} credits available</span>}
          </div>
        )}

        <form onSubmit={submit} data-testid="new-audit-form" className="surface-card hard-shadow p-6 lg:p-8 mb-8">
          <label className="label-overline mb-2 block">Website URL</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              autoFocus
              required
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              data-testid="audit-url-input"
              className="flex-1 bg-white border border-ink rounded-sm px-4 py-3.5 outline-none focus:ring-2 focus:ring-brand mono text-sm"
              placeholder="https://example.com"
            />
            <button
              type="submit"
              disabled={busy || !url.trim()}
              data-testid="audit-submit-button"
              className="inline-flex items-center justify-center gap-2 bg-ink text-white px-6 py-3.5 rounded-sm font-semibold hover:bg-brand transition-all hover:-translate-y-0.5 hard-shadow-sm disabled:opacity-50 disabled:hover:translate-y-0"
            >
              {busy ? "Starting…" : "Run audit"}
              {!busy && <ArrowRight size={16} />}
            </button>
          </div>
          <p className="text-xs text-ink-muted mt-3 mono">
            HTTPS prefix optional · ~15–30 seconds per audit
          </p>
        </form>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {STEPS.map(({ icon: Icon, label }, i) => (
            <div key={label} className="surface-card p-4 fade-in-up" style={{ animationDelay: `${i * 0.05}s` }}>
              <Icon size={20} className="text-brand mb-2" weight="duotone" />
              <div className="label-overline">Check</div>
              <div className="font-semibold text-sm">{label}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 surface-card p-6 bg-secondary border-dashed">
          <div className="label-overline mb-2">Honest results, fast</div>
          <p className="text-sm text-ink-muted leading-relaxed">
            Every audit is a fresh, real-world fetch of the URL you submit —
            same headers, same redirects, same content a visitor would see.
            No cached scores, no synthetic numbers, no guesswork.
          </p>
        </div>
      </div>
    </AppShell>
  );
}
